'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Button, Badge } from '@sarkiai/ui'
import { ArrowRight, Ship } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from '@/components/I18nProvider'

export function CaseStudySection() {
  const t = useTranslations('enterprise_page.case_studies')
  const commonT = useTranslations('common')

  return (
    <section className="py-24 relative overflow-hidden bg-zinc-50 dark:bg-zinc-950/50 px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Text Content Block */}
          <div className="space-y-8 order-2 lg:order-1">
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <Badge variant="outline" className="px-4 py-1 rounded-full text-[10px] uppercase tracking-widest border-black/20 dark:border-white/20 bg-transparent text-foreground">
                  {t('sectionTitle')}
                </Badge>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-4xl lg:text-5xl uppercase tracking-tighter leading-none"
              >
                {t('stdsa.title')}
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex items-start gap-4 pt-4"
              >
                <div className="w-12 h-12 rounded-xl border border-black/10 dark:border-white/20 flex items-center justify-center shrink-0 bg-white dark:bg-zinc-900">
                  <Ship className="w-6 h-6 text-foreground" strokeWidth={1.5} />
                </div>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                  {t('stdsa.description')}
                </p>
              </motion.div>
            </div>

            <div className="flex flex-wrap gap-4 pt-6">
              <Button asChild size="default" className="rounded-2xl px-6 h-10 uppercase tracking-widest text-xs border border-transparent bg-black text-white hover:bg-black/90 transition-all">
                <Link href="/use-cases/stdsa">
                  {t('stdsa.cta')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>

              <Button variant="outline" size="default" className="rounded-2xl px-6 h-10 uppercase tracking-widest text-xs border border-black/40 dark:border-white/40 bg-transparent text-foreground hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all">
                <Link href="/contact">
                  {commonT('talkToExpert')}
                </Link>
              </Button>
            </div>

            {/* Compact Stats */}
            <div className="pt-10 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-black/10 dark:border-white/10">
              <div className="space-y-1">
                <div className="text-2xl text-foreground uppercase tracking-tighter">24/7</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest opacity-60">{t('stats.availability') || 'Disponibilité'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl text-foreground uppercase tracking-tighter">-40%</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest opacity-60">{t('stats.processing') || 'Traitement'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl text-foreground uppercase tracking-tighter">+60%</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest opacity-60">{t('stats.efficiency') || 'Efficacité'}</div>
              </div>
              <div className="space-y-1">
                <div className="text-2xl text-foreground uppercase tracking-tighter">100%</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest opacity-60">{t('stats.sovereignty') || 'Souveraineté'}</div>
              </div>
            </div>
          </div>

          {/* Image Block */}
          <div className="order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative rounded-[2.5rem] overflow-hidden border border-black/5 dark:border-white/10 shadow-2xl aspect-[4/3] lg:aspect-square"
            >
              <Image
                src="/assets/use-cases/stdsa/services/road-freight.jpg"
                alt="STD SA Road Freight"
                fill
                className="object-cover"
                priority
              />
              {/* Ne pas décommenter ce bloc
               <div className="absolute top-6 left-6">
                <Badge className="bg-black/80 backdrop-blur-md border-white/20 text-white px-4 py-1.5 rounded-full text-xs uppercase font-black tracking-widest">
                  {t('stdsa.badge')}
                </Badge>
              </div> */}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
